import { Color, Radius, Spacing } from 'zens-storybook'
import React, { Suspense, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Draft, ZoneData, useStore, ApiError, useZoneMutation } from 'zens-store'
import { useRouter, useNumberParams } from 'zens-router'
import { ZoneEditForm, NavigationDelete, LayoutHeader, ZoneHeader } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

const ZoneEdit = () => {
  const [errors, setErrors] = useState<Record<string, string>>()
  const [confirmDelete, setConfirmDelete] = useState<Draft<ZoneData>>()
  const router = useRouter()
  const { message } = useStore()
  const [{ companyId, zoneId }] = useNumberParams()
  const mutation = useZoneMutation({ companyId })
  const { t } = useTranslation()

  const save = async (draft: Draft<ZoneData>) => {
    try {
      setErrors({})
      if (!draft.data.description) delete draft.data.description
      await mutation.update(draft)
      message.add({
        message: t('Zone was updated successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`ZONE: Zone failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Zone could not be saved')}. ${errorMessage}`,
          status: 'error',
        })
      }
      return
    }

    router.push(`/companies/${companyId}/workspaces/zones/${zoneId}`)
  }

  const remove = async () => {
    try {
      await mutation.delete(confirmDelete!)
      message.add({
        message: t('Zone was deleted successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`ZONE: Zone deletion failed: ${errorMessage}`)
      message.add({
        message: `${t('Zone could not be deleted')}. ${errorMessage}`,
        status: 'error',
      })
      return
    }

    router.push(`/companies/${companyId}/workspaces/zones`)
  }

  return (
    <View style={styles.container}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <ZoneHeader companyId={companyId} zoneId={zoneId} breadcrumbItems={[{ title: t('Edit') }]} />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<ZoneEditForm.Skeleton />}>
          <ZoneEditForm
            companyId={companyId}
            zoneId={zoneId}
            onSave={save}
            onDelete={async (draft) => setConfirmDelete(draft)}
            errors={errors}
          />
        </Suspense>
        <NavigationDelete
          entity={t('Zone')}
          visible={!!confirmDelete}
          onClose={() => setConfirmDelete(undefined)}
          onDelete={remove}
        />
      </View>
    </View>
  )
}

export default ZoneEdit
